import Loader from 'components/Loader';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetWorkingTimeByEmployeeReportQuery } from 'store/api/reportingPerformance';
import { ClassifierData } from 'types/classifiers';
import { WorkingTimeByEmployeeRow, WorkingTimeByEmployeeTotals } from 'types/reporting/performance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';

import {
    ReportEmployeeLink,
    ReportNumberCell,
    ReportTeamLink,
    TONED_COLUMN_META,
    tonedCell,
    GREEN,
} from '../reportCells';
import {
    coreTeamFilter,
    employeeFilter,
    jobRoleFilter,
    optionsFilter,
    periodFilter,
    periodQuickFilter,
    quickFiltersFrom,
    searchSelectors,
} from '../reportFilters';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = WorkingTimeByEmployeeRow;
type Totals = WorkingTimeByEmployeeTotals;

const REPORT_KEY = 'working-time-by-employee';
const USED_CLASSIFIERS = ['WARDYNSKI_EMPLOYMENT_TYPE'];

function buildFilterConfig(classifiers: Record<string, ClassifierData>): FilterConfig {
    const filters: MainFilter[] = [
        periodFilter(),
        coreTeamFilter('coreTeamId', 'reporting.filter.core-team'),
        employeeFilter('employeeId', 'reporting.filter.employee'),
        jobRoleFilter(),
        optionsFilter(
            'employmentType',
            'reporting.filter.employment-type',
            getFilterOptionsFromClassifier(classifiers.WARDYNSKI_EMPLOYMENT_TYPE)
        ),
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-year' }),
        quickFilters: quickFiltersFrom(filters, { employmentType: 'reporting.filter.employment-type' }),
        searchBarSelectors: searchSelectors('employeeName', 'coreTeam'),
        filters,
    };
}

function hoursColumn(id: keyof Totals, headerId: string, rows: Row[] | undefined): DataTableColumnDef<Row, Totals> {
    return {
        id,
        accessorKey: id,
        header: t(headerId),
        meta: {
            ...TONED_COLUMN_META,
            widthWeight: 0.8,
            minPx: 130,
            textAlign: 'right',
            nullHandling: 'nullsLast',
            renderTotal: (totals) => <ReportNumberCell value={totals?.[id]} />,
        },
        cell: tonedCell(rows, (row) => row[id], GREEN),
    };
}

function buildColumns(rows: Row[] | undefined): DataTableColumnDef<Row, Totals>[] {
    return [
        {
            id: 'coreTeam',
            accessorKey: 'coreTeam',
            header: t('reporting.column.core-team'),
            meta: { widthWeight: 1, minPx: 140 },
            cell: ({ row }) => (
                <ReportTeamLink teamId={row.original.coreTeamId}>{row.original.coreTeam}</ReportTeamLink>
            ),
        },
        {
            id: 'employeeName',
            accessorKey: 'employeeName',
            header: t('reporting.column.employee'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.employeeId}>
                    {row.original.employeeName}
                </ReportEmployeeLink>
            ),
        },
        hoursColumn('billableHours', 'reporting.column.billable-hours', rows),
        hoursColumn('nonBillableHours', 'reporting.column.non-billable-hours', rows),
        hoursColumn('potentialClientHours', 'reporting.column.potential-client-hours', rows),
        hoursColumn('businessDevelopmentHours', 'reporting.column.business-development-hours', rows),
        hoursColumn('internalHours', 'reporting.column.internal-hours', rows),
        hoursColumn('totalHours', 'reporting.column.total-hours', rows),
    ];
}

export function WorkingTimeByEmployeeReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetWorkingTimeByEmployeeReportQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const filterConfig = useMemo(() => buildFilterConfig(classifiers), [classifiers]);
    const columns = useMemo(() => buildColumns(data?.content), [data]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)}>
            {isLoadingClassifiers ? (
                <Loader />
            ) : (
                <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                    data={data}
                    belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                    isLoading={isFetching}
                    isError={isError}
                    fetchData={fetchData}
                    filterConfig={filterConfig}
                    columns={columns}
                    columnConfigId={`report-${REPORT_KEY}`}
                    enableColumnPicker
                    enableColumnReorder
                    enableColumnResize
                    pluralTextId="reporting.employees"
                    singularTextId="reporting.employee"
                    noEntriesId="reporting.no-rows"
                    noEntriesBodyId="reporting.no-rows-body"
                />
            )}
        </ReportLayout>
    );
}
